import { prepareForSlot } from '@mui/base'
import { clsx } from 'clsx'
import { type FC } from 'react'

import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { Link, Skeleton } from 'base-ui'

import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../experiments/hooks'
import { useGlobalConfigContext } from '../../../global-config'
import { NextLink } from '../../../next-link'
import { urlGetRelativeUrl } from '../../../url-handling'
import { gkAirAccessorGetLocalizedProductName } from '../../model/gkAirAccessorGetLocalizedProductName'
import { gkAirAccessorGetLocalizedProductUrl } from '../../model/gkAirAccessorGetLocalizedProductUrl'
import { gkAirAccessorGetSponsoredProductLink } from '../../model/gkAirAccessorGetSponsoredProductLink'
import { useGkAirAreaContext } from '../GkAirAreaProvider'

const LinkSlot = prepareForSlot(NextLink)

type GkAirProductNameProps = {
  className?: string
  product?: GkAirRecommendedProductDataWeb
}

export const GkAirProductName: FC<GkAirProductNameProps> = ({
  className,
  product,
}) => {
  const globalConfig = useGlobalConfigContext()
  const { locale } = globalConfig
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const { metadata } = useGkAirAreaContext()

  if (!product) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="w-full" />
        <Skeleton className="w-full" />
        <Skeleton className="w-3/4" />
      </div>
    )
  }

  const sponsoredProductLink = gkAirAccessorGetSponsoredProductLink({
    metadata,
    product,
  })

  const productName = gkAirAccessorGetLocalizedProductName({
    locale,
    product,
  })

  return (
    <Link
      className={clsx('gk-air-product-card-name', className)}
      data-analytics="gk-air-item-title"
      data-qa-id={`form-product-slider-${product.UID}-link-button`}
      href={
        sponsoredProductLink ??
        urlGetRelativeUrl(
          gkAirAccessorGetLocalizedProductUrl({
            locale,
            product,
          }),
        )
      }
      onClick={(): void => {
        trackClick()
      }}
      rel={sponsoredProductLink ? 'nofollow noopener' : undefined}
      slots={{ root: LinkSlot }}
      title={productName}
    >
      {productName}
    </Link>
  )
}
